import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "./MaskedText";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    // Parallax the portrait vs. the text (desktop only visible)
    const yImage = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="relative w-full md:h-[100svh] md:min-h-[720px] md:overflow-hidden"
        >
            {/* ============= MOBILE LAYOUT — stacked, no overlap ============= */}
            <div className="md:hidden flex flex-col">
                {/* Top meta */}
                <div className="px-6 pt-6 flex items-center justify-between font-sans text-xs uppercase tracking-[0.15em] text-neutral-900 font-semibold">
                    <span>A Memorial</span>
                    <span className="tabular">For Danielle</span>
                </div>

                {/* Portrait */}
                <div className="mt-6 mx-6 relative aspect-[4/5] overflow-hidden vignette">
                    <motion.img
                        initial={{ scale: 1.08, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 2, ease: [0.22, 1, 0.36, 1] }}
                        src="/danielle/danielle_smiling.jpg"
                        alt="Danielle Cathleen Alvarez, 1993 — 2021."
                        className="w-full h-full object-cover object-top"
                        data-testid="hero-portrait-mobile"
                    />
                </div>

                {/* Text — below the portrait, no overlap */}
                <div className="px-6 pt-10 pb-14">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6, duration: 1 }}
                        className="font-sans text-xs uppercase tracking-[0.2em] text-neutral-800 font-semibold mb-6"
                    >
                        <span className="inline-block w-6 h-px bg-neutral-700 align-middle mr-3" />
                        Danielle Cathleen Alvarez
                    </motion.div>

                    <h1
                        className="font-serif text-[18vw] leading-[0.88] tracking-[-0.03em] text-neutral-950"
                        data-testid="hero-heading-mobile"
                    >
                        <span className="block">Forty–One</span>
                        <span className="block">Days.</span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.9, duration: 1 }}
                        className="mt-8 font-serif italic text-lg sm:text-xl leading-snug text-neutral-900"
                    >
                        <span className="block">She walked in with a cough.</span>
                        <span className="block">Forty-one days later, she was gone.</span>
                        <span className="block mt-2">
                            Her mother promised to be her voice —{" "}
                            <span className="text-[#981c28] whitespace-nowrap">and she will not be silent.</span>
                        </span>
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.1, duration: 1 }}
                        className="mt-8 flex items-end gap-5 font-sans tabular"
                    >
                        <div>
                            <div className="text-xs uppercase tracking-[0.15em] text-neutral-800 font-semibold mb-1.5">
                                Born
                            </div>
                            <div className="text-xl font-normal text-neutral-950">
                                Aug 5, 1993
                            </div>
                        </div>
                        <span className="pb-1 font-serif text-lg text-neutral-500">
                            —
                        </span>
                        <div>
                            <div className="text-xs uppercase tracking-[0.15em] text-neutral-800 font-semibold mb-1.5">
                                Gone
                            </div>
                            <div className="text-xl font-normal text-neutral-950">
                                Oct 6, 2021
                            </div>
                        </div>
                    </motion.div>

                    <div className="mt-10 flex items-center gap-3 font-sans text-xs uppercase tracking-[0.15em] text-neutral-800 font-semibold">
                        <span>Scroll to remember</span>
                        <motion.span
                            animate={{ y: [0, 6, 0] }}
                            transition={{
                                duration: 2.2,
                                repeat: Infinity,
                                ease: "easeInOut",
                            }}
                            className="block w-px h-6 bg-neutral-700 origin-top"
                        />
                    </div>
                </div>
            </div>

            {/* ============= DESKTOP LAYOUT — parallax portrait right, text left ============= */}
            <div className="hidden md:block h-full">
                {/* Portrait — spotlight framed on the right */}
                <motion.div
                    style={{ y: yImage }}
                    className="absolute inset-0"
                >
                    <div className="absolute left-auto right-[6%] w-[38%] top-[8%] bottom-[8%] overflow-hidden vignette">
                        <motion.img
                            initial={{ scale: 1.12, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{
                                duration: 2.2,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            src="/danielle/danielle_smiling.jpg"
                            alt="Danielle Cathleen Alvarez, 1993 — 2021."
                            className="w-full h-full object-cover object-top"
                            data-testid="hero-portrait"
                        />
                    </div>
                </motion.div>

                {/* Top meta bar */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.9 }}
                    className="absolute top-0 left-0 right-0 z-20 px-12 lg:px-16 pt-8 flex items-center justify-between font-sans text-xs uppercase tracking-[0.15em] text-neutral-900 font-semibold"
                >
                    <span>A Memorial</span>
                    <span className="tabular">Aug 5, 1993 — Oct 6, 2021</span>
                    <span>For Danielle</span>
                </motion.div>

                {/* Text overlay */}
                <motion.div
                    style={{ y: yText, opacity }}
                    className="relative z-10 h-full w-full px-12 lg:px-16 flex flex-col justify-end pb-24"
                >
                    <div className="max-w-[92rem] mx-auto w-full">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 1.4, duration: 1 }}
                            className="font-sans text-sm uppercase tracking-[0.2em] text-neutral-800 font-semibold mb-10"
                        >
                            <span className="inline-block w-8 h-px bg-neutral-700 align-middle mr-3" />
                            Danielle Cathleen Alvarez
                        </motion.div>

                        <MaskedLines
                            as="h1"
                            className="font-serif text-[13vw] lg:text-[11vw] xl:text-[10.4vw] leading-[0.86] tracking-[-0.03em] text-neutral-950"
                            lines={["Forty–One", "Days."]}
                            delayStart={0.15}
                            stagger={0.22}
                            duration={1.25}
                        />

                        <div className="mt-14 grid grid-cols-12 gap-6 items-end">
                            <div className="col-span-5">
                                <motion.p
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1.6, duration: 1.1 }}
                                    className="font-serif italic text-xl lg:text-2xl leading-snug text-neutral-900 max-w-lg"
                                >
                                    <span className="block">She walked in with a cough.</span>
                                    <span className="block">Forty-one days later, she was gone.</span>
                                    <span className="block mt-2">
                                        Her mother promised to be her voice —{" "}
                                        <span className="text-[#981c28] whitespace-nowrap">and she will not be silent.</span>
                                    </span>
                                </motion.p>
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.85, duration: 1 }}
                                    className="mt-8 flex items-end gap-8 font-sans tabular"
                                >
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.15em] text-neutral-800 font-semibold mb-2">
                                            Born
                                        </div>
                                        <div className="text-3xl font-normal text-neutral-950">
                                            Aug 5, 1993
                                        </div>
                                    </div>
                                    <span className="pb-2 font-serif text-xl text-neutral-500">
                                        —
                                    </span>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.15em] text-neutral-800 font-semibold mb-2">
                                            Gone
                                        </div>
                                        <div className="text-3xl font-normal text-neutral-950">
                                            Oct 6, 2021
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 2.4, duration: 1 }}
                            className="mt-20 flex items-center gap-4"
                        >
                            <span className="font-sans text-xs uppercase tracking-[0.15em] text-neutral-800 font-semibold">
                                Scroll to remember
                            </span>
                            <motion.span
                                animate={{ y: [0, 8, 0] }}
                                transition={{
                                    duration: 2.2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                                className="block w-px h-10 bg-neutral-700 origin-top"
                            />
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
